import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import SpellCheckTextarea from '../components/SpellCheckTextarea';
import StoneDetailsSection from '../components/StoneDetailsSection';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';

function EditOrder() {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const { token, API, companyContext } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [loadingOrder, setLoadingOrder] = useState(true);
  const [availableSundries, setAvailableSundries] = useState([]);
  const [memorialPhoto, setMemorialPhoto] = useState(null);
  const [existingPhotoUrl, setExistingPhotoUrl] = useState(null);
  const [letteringRates, setLetteringRates] = useState({});
  const [freeLetters, setFreeLetters] = useState(150);
  const [charCount, setCharCount] = useState(0);
  const [formData, setFormData] = useState({
    order_type: 'new memorial',
    client_id: '',
    client_name: '',
    client_email: '',
    client_phone: '',
    customer_address: '',
    memorial_type: '',
    stone_shape: '',
    stone_type: '',
    stone_code: '',
    stone_colour: '',
    cemetery: '',
    plot_number: '',
    existing_name: '',
    is_book_style: false,
    inscription: '',
    inscription_left: '',
    inscription_right: '',
    existing_inscription: '',  // For add-on: existing text (not charged)
    new_inscription: '',       // For add-on: new text to add (charged)
    font_style: '',
    custom_font: '',
    font_colour: '',
    custom_font_colour: '',
    lettering_type: '',
    lettering_cost: 0,
    ornament: '',
    vase_holes: 'none',
    kerb_surround: false,
    kerb_length: '',
    kerb_width: '',
    kerb_depth: '',
    cover_slab: false,
    cover_slab_length: '',
    cover_slab_width: '',
    cover_slab_depth: '',
    cemetery_fee: '',
    price: '',
    deposit: '',
    vat_rate: '20',
    notes: '',
    leave_space_for_additional: false,
    earliest_fitting_date: '',
    // Maintenance fields
    maintenance_repaint_lettering: false,
    maintenance_relevelling: false,
    maintenance_paint_image: false,
    maintenance_cleaning: false,
    maintenance_repair: false,
    maintenance_other: false,
    maintenance_details: '',
    // Grave vault fields
    vault_material: '',
    vault_availability: '',
    vault_size: '',
    date_required: '',
    service_time: '',
    lid_fitted_by: ''
  });
  const [sundries, setSundries] = useState([{ type: 'select', sundry_id: '', custom_description: '', price: '' }]);

  useEffect(() => {
    fetchSundries();
    fetchOrderData();
    fetchLetteringRates();
  }, []);

  useEffect(() => {
    // Calculate character count (excluding spaces)
    // For add-on inscription orders, only count new_inscription (which is charged)
    const textToCount = formData.order_type === 'add on inscription' 
      ? (formData.new_inscription || '') 
      : (formData.inscription || '');
    const count = textToCount.replace(/\s/g, '').length;
    setCharCount(count);
    
    // Calculate lettering cost
    if (formData.lettering_type && count > freeLetters && letteringRates[formData.lettering_type]) {
      const extraChars = count - freeLetters;
      const cost = extraChars * letteringRates[formData.lettering_type];
      setFormData(prev => ({ ...prev, lettering_cost: parseFloat(cost.toFixed(2)) }));
    } else {
      setFormData(prev => ({ ...prev, lettering_cost: 0 }));
    }
  }, [formData.inscription, formData.new_inscription, formData.order_type, formData.lettering_type, letteringRates, freeLetters]);

  const fetchLetteringRates = async () => {
    try {
      const response = await axios.get(`${API}/settings/lettering-costs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLetteringRates(response.data.costs || {});
      setFreeLetters(response.data.free_characters ?? 150);
    } catch (error) {
      console.error('Failed to fetch lettering rates');
    }
  };

  const fetchOrderData = async () => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const order = response.data;
      
      // Populate form data
      setFormData({
        order_type: order.order_type || 'new memorial',
        client_id: order.client_id || '',
        client_name: order.client_name || '',
        client_email: order.client_email || '',
        client_phone: order.client_phone || '',
        customer_address: order.customer_address || '',
        stone_type: order.stone_type || '',
        stone_code: order.stone_code || '',
        stone_colour: order.stone_colour || '',
        memorial_type: order.memorial_type || '',
        stone_shape: order.stone_shape || '',
        cemetery: order.cemetery || '',
        plot_number: order.plot_number || '',
        existing_name: order.existing_name || '',
        is_book_style: order.is_book_style || false,
        inscription: order.inscription || '',
        inscription_left: order.inscription_left || '',
        inscription_right: order.inscription_right || '',
        existing_inscription: order.existing_inscription || '',
        new_inscription: order.new_inscription || '',
        font_style: order.font_style || '',
        custom_font: order.custom_font || '',
        font_colour: order.font_colour || '',
        custom_font_colour: order.custom_font_colour || '',
        lettering_type: order.lettering_type || '',
        lettering_cost: order.lettering_cost || 0,
        ornament: order.ornament || '',
        vase_holes: order.vase_holes || 'none',
        kerb_surround: order.kerb_surround || false,
        kerb_length: order.kerb_length || '',
        kerb_width: order.kerb_width || '',
        kerb_depth: order.kerb_depth || '',
        cover_slab: order.cover_slab || false,
        cover_slab_length: order.cover_slab_length || '',
        cover_slab_width: order.cover_slab_width || '',
        cover_slab_depth: order.cover_slab_depth || '',
        cemetery_fee: order.cemetery_fee ? order.cemetery_fee.toString() : '',
        price: order.price ? order.price.toString() : '',
        deposit: order.deposit ? order.deposit.toString() : '',
        vat_rate: order.vat_rate ? order.vat_rate.toString() : '20',
        notes: order.notes || '',
        leave_space_for_additional: order.leave_space_for_additional || false,
        earliest_fitting_date: order.earliest_fitting_date || '',
        // Maintenance fields
        maintenance_repaint_lettering: order.maintenance_repaint_lettering || false,
        maintenance_relevelling: order.maintenance_relevelling || false,
        maintenance_paint_image: order.maintenance_paint_image || false,
        maintenance_cleaning: order.maintenance_cleaning || false,
        maintenance_repair: order.maintenance_repair || false,
        maintenance_other: order.maintenance_other || false,
        maintenance_details: order.maintenance_details || '',
        // Grave vault fields
        vault_material: order.vault_material || '',
        vault_availability: order.vault_availability || '',
        vault_size: order.vault_size || '',
        date_required: order.date_required || '',
        service_time: order.service_time || '',
        lid_fitted_by: order.lid_fitted_by || ''
      });
      
      // Store existing memorial photo URL if present
      if (order.memorial_photo_url) {
        setExistingPhotoUrl(order.memorial_photo_url);
      }
      
      // Populate sundries
      if (order.sundries && order.sundries.length > 0) {
        const formattedSundries = order.sundries.map(s => ({
          type: 'custom',
          sundry_id: '',
          custom_description: s.description || '',
          price: s.price ? s.price.toString() : ''
        }));
        setSundries([...formattedSundries, { type: 'select', sundry_id: '', custom_description: '', price: '' }]);
      }
      
      setLoadingOrder(false);
    } catch (error) {
      toast.error('Failed to load order data');
      navigate(`/orders/${orderId}`);
    }
  };

  const fetchSundries = async () => {
    try {
      const response = await axios.get(`${API}/sundries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableSundries(response.data.filter(s => s.is_active));
    } catch (error) {
      console.error('Failed to fetch sundries');
    }
  };

  const handleSundryChange = (index, field, value) => {
    const newSundries = [...sundries];
    
    if (field === 'type') {
      newSundries[index] = { 
        type: value, 
        sundry_id: '', 
        custom_description: '', 
        price: '' 
      };
    } else if (field === 'sundry_id') {
      const selectedSundry = availableSundries.find(s => s.id === value);
      if (selectedSundry) {
        newSundries[index] = {
          ...newSundries[index],
          sundry_id: value,
          price: selectedSundry.price.toString()
        };
      }
    } else {
      newSundries[index][field] = value;
    }
    
    setSundries(newSundries);
    
    // Auto-add new empty sundry if the last one has content
    const lastSundry = newSundries[index];
    if (index === sundries.length - 1) {
      const hasContent = lastSundry.type === 'select' ? lastSundry.sundry_id : (lastSundry.custom_description && lastSundry.price);
      if (hasContent) {
        setSundries([...newSundries, { type: 'select', sundry_id: '', custom_description: '', price: '' }]);
      }
    }
  };

  const removeSundry = (index) => {
    if (sundries.length > 1) {
      const newSundries = sundries.filter((_, i) => i !== index);
      setSundries(newSundries);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customer_address || !formData.customer_address.trim()) {
      toast.error('Customer address is required');
      return;
    }
    setLoading(true);

    try {
      // Filter out empty sundries and map to proper format
      const validSundries = sundries.filter(s => {
        if (s.type === 'select') {
          return s.sundry_id;
        } else {
          return s.custom_description.trim() && s.price;
        }
      }).map(s => {
        if (s.type === 'select') {
          const sundry = availableSundries.find(as => as.id === s.sundry_id);
          return {
            description: sundry.description,
            price: parseFloat(sundry.price)
          };
        } else {
          return {
            description: s.custom_description,
            price: parseFloat(s.price)
          };
        }
      });
      
      const payload = {
        ...formData,
        price: parseFloat(formData.price),
        deposit: parseFloat(formData.deposit) || 0,
        cemetery_fee: parseFloat(formData.cemetery_fee) || 0,
        vat_rate: parseFloat(formData.vat_rate),
        sundries: validSundries
      };

      // Use PUT to update the existing order
      await axios.put(`${API}/orders/${orderId}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Upload memorial photo if provided
      if (memorialPhoto) {
        const photoFormData = new FormData();
        photoFormData.append('file', memorialPhoto);
        
        try {
          await axios.post(`${API}/orders/${orderId}/memorial-photo`, photoFormData, {
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          });
        } catch (photoError) {
          console.error('Photo upload failed:', photoError);
          toast.warning('Order created but photo upload failed');
        }
      }

      toast.success('Order updated successfully');
      navigate(`/orders/${orderId}`);
    } catch (error) {
      const detail = error.response?.data?.detail; const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to update order'); toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (loadingOrder) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4 flex items-center justify-center">
        <p>Loading order data...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate(`/orders/${orderId}`)}
          className="mb-6"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Order
        </Button>

        <Card className="border-0 shadow-xl">
          <CardHeader>
            <CardTitle className="text-3xl">Edit Order</CardTitle>
            <CardDescription>Update the details for this memorial order</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="edit-order-form">
              {/* Order Type */}
              <div className="rounded-lg border-l-4 border-purple-500 bg-purple-50 p-5 space-y-4">
                <h3 className="text-lg font-semibold text-purple-900 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                  Order Type
                </h3>
                <div className="space-y-2">
                  <Label htmlFor="order_type" className="text-purple-800">Type *</Label>
                  <select
                    id="order_type"
                    value={formData.order_type}
                    onChange={(e) => setFormData({ ...formData, order_type: e.target.value })}
                    className="w-full px-3 py-2 border border-purple-200 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
                    required
                    data-testid="order-type-select"
                  >
                    <option value="new memorial">New Memorial</option>
                    <option value="restoration">Restoration</option>
                    <option value="add on inscription">Add On Inscription</option>
                    <option value="maintenance">Maintenance</option>
                    {(companyContext?.features?.grave_vaults_enabled || formData.order_type === 'grave vault') && (
                      <option value="grave vault">Grave Vault</option>
                    )}
                  </select>
                </div>
              </div>

              {/* Client Information */}
              <div className="rounded-lg border-l-4 border-blue-500 bg-blue-50 p-5 space-y-4">
                <h3 className="text-lg font-semibold text-blue-900 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                  Client Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="client_name" className="text-blue-800">Client Name *</Label>
                    <Input
                      id="client_name"
                      value={formData.client_name}
                      onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                      required
                      data-testid="client-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="client_email">Email *</Label>
                    <Input
                      id="client_email"
                      type="email"
                      value={formData.client_email}
                      onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                      required
                      data-testid="client-email-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="client_phone">Phone</Label>
                  <Input
                    id="client_phone"
                    value={formData.client_phone}
                    onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })}
                    data-testid="client-phone-input"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="customer_address">Customer Address *</Label>
                  <textarea
                    id="customer_address"
                    value={formData.customer_address}
                    onChange={(e) => setFormData({ ...formData, customer_address: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="2"
                    required
                    data-testid="customer-address-input"
                  />
                </div>
              </div>

              {/* Stone Details - hidden for grave vault and maintenance */}
              {formData.order_type !== 'grave vault' && formData.order_type !== 'maintenance' && (
                <StoneDetailsSection formData={formData} setFormData={setFormData} />
              )}

              {/* Cemetery / Location */}
              <div className="rounded-lg border-l-4 border-emerald-500 bg-emerald-50 p-5 space-y-4">
                <h3 className="text-lg font-semibold text-emerald-900 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                  Cemetery / Location
                </h3>
                <div className={`grid grid-cols-1 ${formData.order_type === 'grave vault' ? 'md:grid-cols-2' : ''} gap-4`}>
                  <div className="space-y-2">
                    <Label htmlFor="cemetery">Cemetery *</Label>
                    <Input
                      id="cemetery"
                      value={formData.cemetery}
                      onChange={(e) => setFormData({ ...formData, cemetery: e.target.value })}
                      required
                      data-testid="cemetery-input"
                    />
                  </div>
                  {formData.order_type === 'grave vault' && (
                    <div className="space-y-2">
                      <Label htmlFor="plot_number">Plot Number / Reference</Label>
                      <Input
                        id="plot_number"
                        value={formData.plot_number || ''}
                        onChange={(e) => setFormData({ ...formData, plot_number: e.target.value })}
                        placeholder="e.g., Section B, Row 3, Plot 14"
                        data-testid="plot-number-input"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Grave Vault Details - only for grave vault orders */}
              {formData.order_type === 'grave vault' && (
                <div className="rounded-lg border-l-4 border-indigo-500 bg-indigo-50 p-5 space-y-4">
                  <h3 className="text-lg font-semibold text-indigo-900 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-indigo-500"></span>
                    Grave Vault Details
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="vault_material" className="text-indigo-800">Vault Material</Label>
                      <select
                        id="vault_material"
                        value={formData.vault_material}
                        onChange={(e) => setFormData({ ...formData, vault_material: e.target.value })}
                        className="flex h-10 w-full rounded-md border border-indigo-200 bg-background px-3 py-2 text-sm focus:ring-indigo-500"
                        data-testid="vault-material-select"
                      >
                        <option value="">Select Material</option>
                        <option value="concrete">Concrete</option>
                        <option value="brick">Brick</option>
                        <option value="stone">Stone</option>
                        <option value="composite">Composite</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="vault_availability" className="text-indigo-800">Availability</Label>
                      <select
                        id="vault_availability"
                        value={formData.vault_availability}
                        onChange={(e) => setFormData({ ...formData, vault_availability: e.target.value })}
                        className="flex h-10 w-full rounded-md border border-indigo-200 bg-background px-3 py-2 text-sm focus:ring-indigo-500"
                        data-testid="vault-availability-select"
                      >
                        <option value="">Select Availability</option>
                        <option value="in-stock">In Stock</option>
                        <option value="made-to-order">Made to Order</option>
                        <option value="check-supplier">Check with Supplier</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="vault_size" className="text-indigo-800">External Size</Label>
                      <Input
                        id="vault_size"
                        value={formData.vault_size}
                        onChange={(e) => setFormData({ ...formData, vault_size: e.target.value })}
                        placeholder="e.g., 7ft x 2ft 6in x 2ft"
                        className="border-indigo-200 focus:ring-indigo-500"
                        data-testid="vault-size-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="date_required" className="text-indigo-800">Date Required</Label>
                      <Input
                        id="date_required"
                        type="date"
                        value={formData.date_required}
                        onChange={(e) => setFormData({ ...formData, date_required: e.target.value })}
                        className="border-indigo-200 focus:ring-indigo-500"
                        data-testid="date-required-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="service_time" className="text-indigo-800">Time of Service</Label>
                      <Input
                        id="service_time"
                        type="time"
                        value={formData.service_time}
                        onChange={(e) => setFormData({ ...formData, service_time: e.target.value })}
                        className="border-indigo-200 focus:ring-indigo-500"
                        data-testid="service-time-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lid_fitted_by" className="text-indigo-800">Who Will Fit the Lid?</Label>
                      <Input
                        id="lid_fitted_by"
                        value={formData.lid_fitted_by}
                        onChange={(e) => setFormData({ ...formData, lid_fitted_by: e.target.value })}
                        placeholder="e.g., Company name or self"
                        className="border-indigo-200 focus:ring-indigo-500"
                        data-testid="lid-fitted-by-input"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Inscription - only for new memorial and add on inscription */}
              {(formData.order_type === 'new memorial' || formData.order_type === 'add on inscription') && (
                <div className="rounded-lg border-l-4 border-amber-500 bg-amber-50 p-5 space-y-4">
                  <h3 className="text-lg font-semibold text-amber-900 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                    Inscription Details
                  </h3>
                  
                  {/* Existing Name - only for add on inscription */}
                  {formData.order_type === 'add on inscription' && (
                    <div className="space-y-2">
                      <Label htmlFor="existing_name" className="text-amber-800">Existing Name on the Memorial</Label>
                      <Input
                        id="existing_name"
                        value={formData.existing_name}
                        onChange={(e) => setFormData({ ...formData, existing_name: e.target.value })}
                        placeholder="Enter the name(s) already on the memorial..."
                        className="border-amber-200 focus:ring-amber-500"
                        data-testid="existing-name-input"
                      />
                      <p className="text-sm text-amber-700">
                        This helps identify the correct memorial for the add-on inscription.
                      </p>
                    </div>
                  )}

                  {/* For Add-On Inscriptions: Two separate text boxes */}
                  {formData.order_type === 'add on inscription' ? (
                    <div className="space-y-4">
                      {/* Existing Inscription - Not charged */}
                      <div className="space-y-2 p-4 bg-slate-100 rounded-lg border border-slate-300">
                        <Label htmlFor="existing_inscription" className="text-slate-700 flex items-center gap-2">
                          <span className="px-2 py-0.5 bg-slate-500 text-white text-xs rounded">EXISTING</span>
                          Current Inscription on Memorial
                        </Label>
                        <SpellCheckTextarea
                          id="existing_inscription"
                          value={formData.existing_inscription}
                          onChange={(e) => setFormData({ ...formData, existing_inscription: e.target.value })}
                          rows={4}
                          placeholder="Enter the existing inscription text that's already on the memorial..."
                          data-testid="existing-inscription-input"
                          className="font-serif text-center bg-white"
                        />
                        <p className="text-sm text-slate-500">
                          This is for reference only - no charge for existing text.
                        </p>
                      </div>

                      {/* New Inscription - Charged */}
                      <div className="space-y-2 p-4 bg-green-50 rounded-lg border border-green-300">
                        <Label htmlFor="new_inscription" className="text-green-800 flex items-center gap-2">
                          <span className="px-2 py-0.5 bg-green-600 text-white text-xs rounded">NEW</span>
                          New Inscription to Add *
                        </Label>
                        <SpellCheckTextarea
                          id="new_inscription"
                          value={formData.new_inscription}
                          onChange={(e) => setFormData({ ...formData, new_inscription: e.target.value })}
                          rows={4}
                          placeholder="Enter the new inscription text to be added..."
                          data-testid="new-inscription-input"
                          className="font-serif text-center bg-white border-green-200"
                        />
                        <div className="flex justify-between items-center text-sm">
                          <p className="text-green-700">
                            This text will be charged based on lettering rates.
                          </p>
                          <div className="font-semibold text-green-700">
                            Characters: {charCount} {charCount > freeLetters && <span className="text-amber-600">(+{charCount - freeLetters} extra)</span>}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* Book Style Memorial Checkbox - Only for new memorials */}
                      <div className="p-3 bg-amber-100 border border-amber-300 rounded-lg">
                        <label className="flex items-center space-x-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.is_book_style}
                            onChange={(e) => setFormData({ ...formData, is_book_style: e.target.checked })}
                            className="w-5 h-5 rounded border-amber-400 text-amber-600 focus:ring-amber-500"
                            data-testid="book-style-checkbox"
                          />
                          <span className="font-medium text-amber-900">Book Style Memorial</span>
                        </label>
                        <p className="text-sm text-amber-700 mt-1 ml-8">Check this for memorials with left and right pages</p>
                      </div>

                      {/* Single Inscription or Book Style (Left/Right Pages) */}
                      {!formData.is_book_style ? (
                        <div className="space-y-2">
                          <Label htmlFor="inscription">
                            Inscription Text *
                          </Label>
                          <SpellCheckTextarea
                            id="inscription"
                            value={formData.inscription}
                            onChange={(e) => setFormData({ ...formData, inscription: e.target.value })}
                            rows={6}
                            placeholder="Enter the inscription text exactly as it should appear on the memorial..."
                            data-testid="inscription-input"
                            className="font-serif text-center"
                          />
                          <div className="flex justify-between items-center text-sm">
                            <p className="text-slate-500">
                              Please enter the exact text, including line breaks, capitalization, and punctuation.
                            </p>
                            <div className="font-semibold text-blue-600">
                              Total Characters: {charCount} {charCount > freeLetters && <span className="text-amber-600">(+{charCount - freeLetters} extra)</span>}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="inscription_left">
                              Left Page *
                            </Label>
                            <SpellCheckTextarea
                              id="inscription_left"
                              value={formData.inscription_left}
                              onChange={(e) => setFormData({ ...formData, inscription_left: e.target.value })}
                              rows={6}
                              placeholder="Enter text for the left page..."
                              data-testid="inscription-left-input"
                              className="font-serif text-center"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="inscription_right">
                              Right Page *
                            </Label>
                            <SpellCheckTextarea
                              id="inscription_right"
                              value={formData.inscription_right}
                              onChange={(e) => setFormData({ ...formData, inscription_right: e.target.value })}
                              rows={6}
                              placeholder="Enter text for the right page..."
                              data-testid="inscription-right-input"
                              className="font-serif text-center"
                            />
                          </div>
                          <div className="md:col-span-2 text-sm text-slate-500">
                            <p>Enter each page's text exactly as it should appear. Text will be centered on each page.</p>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {/* Lettering Type */}
                  <div className="space-y-2">
                    <Label htmlFor="lettering_type">Lettering Type / Colour</Label>
                    <select
                      id="lettering_type"
                      data-testid="lettering-type-select"
                      value={formData.lettering_type}
                      onChange={(e) => setFormData({ ...formData, lettering_type: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select lettering type/colour...</option>
                      {Object.entries(letteringRates).map(([key, rate]) => (
                        <option key={key} value={key}>
                          {key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())} (£{rate || 0}/char)
                        </option>
                      ))}
                      {formData.lettering_type && formData.lettering_type !== 'custom' && !(formData.lettering_type in letteringRates) && (
                        <option value={formData.lettering_type}>
                          {formData.lettering_type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())} (no longer in settings)
                        </option>
                      )}
                      <option value="custom">Custom (specify below)</option>
                    </select>
                    {formData.lettering_type === 'custom' && (
                      <Input
                        placeholder="Enter custom lettering type/colour"
                        value={formData.custom_font_colour || ''}
                        onChange={(e) => setFormData({ ...formData, custom_font_colour: e.target.value })}
                        className="mt-2"
                      />
                    )}
                    {formData.lettering_cost > 0 && (
                      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-sm font-semibold text-blue-900">
                          Lettering Cost: £{formData.lettering_cost.toFixed(2)}
                        </p>
                        <p className="text-xs text-blue-700">
                          First {freeLetters} characters free. Extra {charCount - freeLetters} characters @ £{letteringRates[formData.lettering_type]}/char
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Leave Space for Additional Inscription */}
                  <div className="flex items-center space-x-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <input
                      type="checkbox"
                      id="leave_space_for_additional"
                      checked={formData.leave_space_for_additional}
                      onChange={(e) => setFormData({ ...formData, leave_space_for_additional: e.target.checked })}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      data-testid="leave-space-checkbox"
                    />
                    <Label htmlFor="leave_space_for_additional" className="text-sm font-medium text-amber-900 cursor-pointer">
                      Leave room for additional inscription (e.g., for future dates or names)
                    </Label>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="font_style">Font Style</Label>
                      <select
                        id="font_style"
                        value={formData.font_style}
                        onChange={(e) => setFormData({ ...formData, font_style: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                        data-testid="font-style-select"
                      >
                        <option value="">Select font style</option>
                        <option value="serif-classic">Serif Classic</option>
                        <option value="serif-elegant">Serif Elegant</option>
                        <option value="serif-traditional">Serif Traditional</option>
                        <option value="sans-serif-modern">Sans-Serif Modern</option>
                        <option value="script-flowing">Script Flowing</option>
                        <option value="gothic">Gothic</option>
                        <option value="roman">Roman</option>
                        <option value="custom">Custom</option>
                      </select>
                      {formData.font_style === 'custom' && (
                        <Input
                          placeholder="Enter custom font name"
                          value={formData.custom_font || ''}
                          onChange={(e) => setFormData({ ...formData, custom_font: e.target.value })}
                          className="mt-2"
                          data-testid="custom-font-input"
                        />
                      )}
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="ornament">Ornament/Symbol</Label>
                      <Input
                        id="ornament"
                        value={formData.ornament}
                        onChange={(e) => setFormData({ ...formData, ornament: e.target.value })}
                        placeholder="e.g., Rose, Cross, Angel, Custom design..."
                        data-testid="ornament-input"
                      />
                      <p className="text-xs text-slate-500">Enter any ornament or symbol description</p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="vase_holes">Vase Holes Required</Label>
                      <select
                        id="vase_holes"
                        value={formData.vase_holes}
                        onChange={(e) => setFormData({ ...formData, vase_holes: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                        data-testid="vase-holes-select"
                      >
                        <option value="none">None</option>
                        <option value="left">Left</option>
                        <option value="right">Right</option>
                        <option value="left and right">Left and Right</option>
                        <option value="centre">Centre</option>
                        <option value="rear">Rear</option>
                      </select>
                      <p className="text-xs text-slate-500">Select vase hole drilling requirements</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Sundries */}
              <div className="rounded-lg border-l-4 border-teal-500 bg-teal-50 p-5 space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-teal-900 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-teal-500"></span>
                    Sundries (Additional Items)
                  </h3>
                  <p className="text-xs text-teal-600 mt-1">Select from saved items or add custom ones (VAT will be applied). Empty rows will be ignored.</p>
                </div>
                {sundries.map((sundry, index) => (
                  <div key={index} className="space-y-2">
                    <div className="flex gap-2 items-start">
                      <div className="w-32">
                        <select
                          className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          value={sundry.type}
                          onChange={(e) => handleSundryChange(index, 'type', e.target.value)}
                        >
                          <option value="select">Select Item</option>
                          <option value="custom">Custom</option>
                        </select>
                      </div>
                      
                      {sundry.type === 'select' ? (
                        <>
                          <div className="flex-1">
                            <select
                              className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              value={sundry.sundry_id}
                              onChange={(e) => handleSundryChange(index, 'sundry_id', e.target.value)}
                            >
                              <option value="">Choose sundry...</option>
                              {availableSundries.map(s => (
                                <option key={s.id} value={s.id}>
                                  {s.description} - £{s.price.toFixed(2)}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="w-32">
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="£0.00"
                              value={sundry.price}
                              disabled
                            />
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="flex-1">
                            <Input
                              placeholder="Description (e.g., Special engraving)"
                              value={sundry.custom_description}
                              onChange={(e) => handleSundryChange(index, 'custom_description', e.target.value)}
                            />
                          </div>
                          <div className="w-32">
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="£0.00"
                              value={sundry.price}
                              onChange={(e) => handleSundryChange(index, 'price', e.target.value)}
                            />
                          </div>
                        </>
                      )}
                      
                      {sundries.length > 1 && (sundry.sundry_id || sundry.custom_description) && (
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="sm"
                          onClick={() => removeSundry(index)}
                        >
                          ×
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Pricing */}
              <div className="rounded-lg border-l-4 border-green-500 bg-green-50 p-5 space-y-4">
                <h3 className="text-lg font-semibold text-green-900 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-green-500"></span>
                  Pricing
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="price">Total Price *</Label>
                    <Input
                      id="price"
                      type="number"
                      step="0.01"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      required
                      data-testid="price-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="deposit">Deposit</Label>
                    <Input
                      id="deposit"
                      type="number"
                      step="0.01"
                      value={formData.deposit}
                      onChange={(e) => setFormData({ ...formData, deposit: e.target.value })}
                      placeholder="0"
                      data-testid="deposit-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="cemetery_fee">Cemetery/Church Fee (VAT Exempt)</Label>
                    <Input
                      id="cemetery_fee"
                      type="number"
                      step="0.01"
                      value={formData.cemetery_fee}
                      onChange={(e) => setFormData({ ...formData, cemetery_fee: e.target.value })}
                      placeholder="0.00"
                      data-testid="cemetery-fee-input"
                    />
                    <p className="text-xs text-slate-500">This amount will not have VAT added</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="vat_rate">VAT Rate (%)</Label>
                    <Input
                      id="vat_rate"
                      type="number"
                      step="0.1"
                      value={formData.vat_rate}
                      onChange={(e) => setFormData({ ...formData, vat_rate: e.target.value })}
                      required
                      data-testid="vat-rate-input"
                    />
                    <p className="text-xs text-slate-500">Default is 20% (UK standard rate)</p>
                  </div>
                </div>
              </div>

              {/* Memorial Photo (only for restoration, add on inscription, and maintenance) */}
              {(formData.order_type === 'restoration' || formData.order_type === 'add on inscription' || formData.order_type === 'maintenance') && (
                <div className="rounded-lg border-l-4 border-rose-500 bg-rose-50 p-5 space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold text-rose-900 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-rose-500"></span>
                      Memorial Photo
                    </h3>
                    <p className="text-xs text-rose-600 mt-1">Upload a photo of the memorial before work (Optional)</p>
                  </div>
                  <div className="space-y-2">
                    {/* Show existing photo if present */}
                    {existingPhotoUrl && !memorialPhoto && (
                      <div className="mb-3">
                        <p className="text-sm text-slate-600 mb-2">Current photo:</p>
                        <img 
                          src={existingPhotoUrl.startsWith('data:') ? existingPhotoUrl : `${API}${existingPhotoUrl}`} 
                          alt="Memorial before work" 
                          className="max-w-xs max-h-48 rounded-lg border border-slate-200 shadow-sm"
                        />
                        <p className="text-xs text-slate-500 mt-1">Upload a new photo below to replace the current one</p>
                      </div>
                    )}
                    <Label htmlFor="memorial-photo">
                      {existingPhotoUrl ? 'Replace Photo (Optional)' : 'Photo of Memorial (Before Work)'}
                    </Label>
                    <Input
                      id="memorial-photo"
                      type="file"
                      accept="image/*"
                      onChange={(e) => setMemorialPhoto(e.target.files[0])}
                      data-testid="memorial-photo-input"
                    />
                    {memorialPhoto && (
                      <p className="text-sm text-green-600">✓ New photo selected: {memorialPhoto.name}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Maintenance Work Required - only for maintenance orders */}
              {formData.order_type === 'maintenance' && (
                <div className="rounded-lg border-l-4 border-orange-500 bg-orange-50 p-5 space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold text-orange-900 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                      Maintenance Work Required
                    </h3>
                    <p className="text-xs text-orange-600 mt-1">Select the maintenance work to be carried out</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <label className="flex items-center gap-2 p-2 bg-orange-100 rounded border border-orange-200 cursor-pointer hover:bg-orange-200 transition-colors">
                      <input
                        type="checkbox"
                        checked={formData.maintenance_repaint_lettering || false}
                        onChange={(e) => setFormData({ ...formData, maintenance_repaint_lettering: e.target.checked })}
                        className="w-4 h-4 rounded border-orange-400 text-orange-600 focus:ring-orange-500"
                      />
                      <span className="text-sm text-orange-800">Repaint Lettering</span>
                    </label>
                    <label className="flex items-center gap-2 p-2 bg-orange-100 rounded border border-orange-200 cursor-pointer hover:bg-orange-200 transition-colors">
                      <input
                        type="checkbox"
                        checked={formData.maintenance_relevelling || false}
                        onChange={(e) => setFormData({ ...formData, maintenance_relevelling: e.target.checked })}
                        className="w-4 h-4 rounded border-orange-400 text-orange-600 focus:ring-orange-500"
                      />
                      <span className="text-sm text-orange-800">Re-levelling</span>
                    </label>
                    <label className="flex items-center gap-2 p-2 bg-orange-100 rounded border border-orange-200 cursor-pointer hover:bg-orange-200 transition-colors">
                      <input
                        type="checkbox"
                        checked={formData.maintenance_paint_image || false}
                        onChange={(e) => setFormData({ ...formData, maintenance_paint_image: e.target.checked })}
                        className="w-4 h-4 rounded border-orange-400 text-orange-600 focus:ring-orange-500"
                      />
                      <span className="text-sm text-orange-800">Paint Image/Ornament</span>
                    </label>
                    <label className="flex items-center gap-2 p-2 bg-orange-100 rounded border border-orange-200 cursor-pointer hover:bg-orange-200 transition-colors">
                      <input
                        type="checkbox"
                        checked={formData.maintenance_cleaning || false}
                        onChange={(e) => setFormData({ ...formData, maintenance_cleaning: e.target.checked })}
                        className="w-4 h-4 rounded border-orange-400 text-orange-600 focus:ring-orange-500"
                      />
                      <span className="text-sm text-orange-800">Cleaning</span>
                    </label>
                    <label className="flex items-center gap-2 p-2 bg-orange-100 rounded border border-orange-200 cursor-pointer hover:bg-orange-200 transition-colors">
                      <input
                        type="checkbox"
                        checked={formData.maintenance_repair || false}
                        onChange={(e) => setFormData({ ...formData, maintenance_repair: e.target.checked })}
                        className="w-4 h-4 rounded border-orange-400 text-orange-600 focus:ring-orange-500"
                      />
                      <span className="text-sm text-orange-800">Repair Work</span>
                    </label>
                    <label className="flex items-center gap-2 p-2 bg-orange-100 rounded border border-orange-200 cursor-pointer hover:bg-orange-200 transition-colors">
                      <input
                        type="checkbox"
                        checked={formData.maintenance_other || false}
                        onChange={(e) => setFormData({ ...formData, maintenance_other: e.target.checked })}
                        className="w-4 h-4 rounded border-orange-400 text-orange-600 focus:ring-orange-500"
                      />
                      <span className="text-sm text-orange-800">Other</span>
                    </label>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="maintenance_details" className="text-orange-800">Work Details</Label>
                    <textarea
                      id="maintenance_details"
                      value={formData.maintenance_details || ''}
                      onChange={(e) => setFormData({ ...formData, maintenance_details: e.target.value })}
                      rows={3}
                      placeholder="Describe the specific maintenance work to be carried out..."
                      className="w-full px-3 py-2 border border-orange-200 rounded-md focus:ring-orange-500"
                    />
                  </div>
                </div>
              )}

              {/* Earliest Fitting Date - only for add on inscription orders */}
              {formData.order_type === 'add on inscription' && (
                <div className="rounded-lg border-l-4 border-violet-500 bg-violet-50 p-5 space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold text-violet-900 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-violet-500"></span>
                      Earliest Fitting Date
                    </h3>
                    <p className="text-xs text-violet-600 mt-1">Authority restriction - memorial cannot be fitted before this date (Optional)</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="earliest_fitting_date">Do Not Fit Before</Label>
                    <Input
                      id="earliest_fitting_date"
                      type="date"
                      value={formData.earliest_fitting_date}
                      onChange={(e) => setFormData({ ...formData, earliest_fitting_date: e.target.value })}
                      data-testid="earliest-fitting-date-input"
                    />
                    {formData.earliest_fitting_date && (
                      <p className="text-sm text-purple-600">
                        📅 Memorial cannot be fitted before: {new Date(formData.earliest_fitting_date).toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Notes */}
              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={4}
                  data-testid="notes-input"
                />
              </div>

              <div className="flex gap-4 pt-4">
                <Button type="submit" disabled={loading} className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600" data-testid="submit-order-btn">
                  {loading ? 'Updating...' : 'Update Order'}
                </Button>
                <Button type="button" variant="outline" onClick={() => navigate(`/orders/${orderId}`)} disabled={loading}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default EditOrder;